import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Typography,
  Box,
  Container,
  Section,
  Layout,
  Grid,
  Button,
  Link,
  Image,
} from '../components';
import styled from 'styled-components';
import ServiceHeroSection from '../components/ServiceHeroSection';
import bannerImage from '../images/services/managedItServices/managedItServicesBanner.svg';
import applicationSupport from '../images/services/managedItServices/applicationSupport.svg';
import itHelpDesk from '../images/services/managedItServices/ItHelpDesk.svg';
import backupandRecovery from '../images/services/managedItServices/backupandRecovery.svg';
import deviceManagement from '../images/services/managedItServices/deviceManagement.svg';
import Flex from './../components/Flex';
import { AnimatePresence, motion } from 'framer-motion';
import { stats, technologies } from '../config/data';

const ServiceContentImage = styled.img`
  box-shadow: 25px 25px 0px 0px var(--secondary-color);
  margin: 25px 0;
  width: 100%;

  @media screen and (max-width: 768px) {
    box-shadow: 10px 10px 0px 0px var(--secondary-color);
  }
`;

const MotionBox = motion(
  styled.div`
    border: 1px solid #152539;
    cursor: pointer;
    background-color: var(--tech-bg);
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  `
);
const ValueBox = styled(Box)`
  background-color: #061324;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 10px;
  height: 150px;

  @media (max-width: 768px) {
    padding: 15px;
    margin: 5px;
  }
`;
const techBoxVariants = {
  initial: { opacity: 0, y: 20 },
  hover: { scale: 1.1 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};
const GridContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-direction: column;

  @media (min-width: 600px) {
    flex-direction: row;
  }
`;
const managedItServices = () => {
  return (
    <Layout title="Managed IT Services">
      <Helmet>
        <meta
          name="description"
          content="Reliable IT infrastructure management by certified experts. Proactive security, seamless support, backup solutions, and device management for optimized functionality and data protection."
        />
      </Helmet>
      <Container mt={{ xs: 8, md: 12 }}>
        <ServiceHeroSection
          title={'Managed IT Services'}
          imageSrc={bannerImage}
          imgAlt={'Managed IT Services'}
        />
        <Section>
          <Typography my={5} variant="paragraph1">
            Get our qualified and certified team of experts to handle your IT
            infrastructure. We proactively ensure maximum security, hassle-free
            troubleshooting, optimum functionality, highest server uptime, and
            reliable backups.
          </Typography>

          <Grid
            gap="150px"
            gapSm="15px"
            alignItems="center"
            gridTemplateColumns={{ _: '1fr', sm: '3fr 3fr' }}
            mb={3}
          >
            <Box>
              <ServiceContentImage src={applicationSupport} />
            </Box>
            <Box>
              <Typography mt={2} variant="heading5">
                Application Support
              </Typography>

              <Typography mt={2} variant="paragraph1">
                We monitor your applications for performance issues,
                troubleshoot problems, and apply updates to keep them
                functioning smoothly.
              </Typography>
            </Box>
          </Grid>

          <Grid
            gap="150px"
            gapSm="15px"
            alignItems="center"
            gridTemplateColumns={{ _: '1fr', sm: '3fr 3fr' }}
            mb={3}
          >
            <Box flip={true}>
              <Typography mt={2} variant="heading5">
                IT Help Desk
              </Typography>

              <Typography mt={2} variant="paragraph1">
                Our technicians are highly responsive to solve queries,
                troubleshoot problems, and resolve technical issues promptly and
                efficiently.
              </Typography>
            </Box>
            <Box>
              <ServiceContentImage
                style={{ float: 'right' }}
                src={itHelpDesk}
              />
            </Box>
          </Grid>

          <Grid
            gap="150px"
            gapSm="15px"
            alignItems="center"
            gridTemplateColumns={{ _: '1fr', sm: '3fr 3fr' }}
            mb={3}
          >
            <Box>
              <ServiceContentImage src={backupandRecovery} />
            </Box>
            <Box>
              <Typography mt={2} variant="heading5">
                Backup and Recovery
              </Typography>

              <Typography mt={2} variant="paragraph1">
                We offer robust backup solutions and swift recovery services to
                safeguard your valuable information against any unforeseen
                events.
              </Typography>
            </Box>
          </Grid>

          <Grid
            gap="150px"
            gapSm="15px"
            alignItems="center"
            gridTemplateColumns={{ _: '1fr', sm: '3fr 3fr' }}
            mb={3}
          >
            <Box flip={true}>
              <Typography mt={2} variant="heading5">
                Device Management
              </Typography>

              <Typography mt={2} variant="paragraph1">
                We take care of device provisioning, configuration, security
                patching, and monitoring so all your devices are secure,
                compliant, and functioning optimally.
              </Typography>
            </Box>
            <Box>
              <ServiceContentImage
                style={{ float: 'right' }}
                src={deviceManagement}
              />
            </Box>
          </Grid>
        </Section>
        <Section>
          <Grid gridTemplateColumns={{ _: '1fr', sm: '1fr' }}>
            <Box>
              <Typography
                style={{ textAlign: 'center' }}
                variant="heading3"
                mb={3}
              >
                How Experienced We Are
              </Typography>
            </Box>
          </Grid>
          <Grid
            gridTemplateColumns={{
              _: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr 1fr',
            }}
          >
            {stats.map(stat => (
              <ValueBox key={stat.label}>
                <Typography variant="heading3">{stat.value}</Typography>
                <Typography variant="paragraph1">{stat.label}</Typography>
              </ValueBox>
            ))}
          </Grid>
        </Section>
        <Section>
          <Grid
            gridTemplateColumns={{ _: '1fr', sm: '1fr' }}
            justifyItems="center"
          >
            <Box>
              <Typography
                style={{ textAlign: 'center' }}
                variant="heading3"
                mb={3}
              >
                Technologies We Have Expertise In
              </Typography>
            </Box>
          </Grid>
          <Flex>
            <AnimatePresence>
              {technologies
                .filter(tech => tech.category === 'Cloud')
                .map((tech, index) => (
                  <div key={index}>
                    <MotionBox
                      initial="initial"
                      animate="enter"
                      exit="exit"
                      whileHover="hover"
                      variants={techBoxVariants}
                      transition={{ duration: 0.5 }}
                    >
                      <Image width="100px" src={tech.src} alt={tech.alt} />
                    </MotionBox>
                  </div>
                ))}
            </AnimatePresence>
          </Flex>
        </Section>
        <Section>
          <Grid gridTemplateColumns={{ _: '1fr', sm: '1fr' }}>
            <Box>
              <Typography
                style={{ textAlign: 'center' }}
                variant="heading3"
                mb={3}
              >
                Ready To Build Your Brand?
              </Typography>
            </Box>
          </Grid>
          <GridContainer>
            <ButtonContainer>
              <Link to="/contact/">
                <Button variant="contained" color="primary" primary>
                  Contact Us
                </Button>
              </Link>
              <Link to="/expertise/">
                <Button variant="contained" color="primary">
                  View All Services
                </Button>
              </Link>
            </ButtonContainer>
          </GridContainer>
        </Section>
      </Container>
    </Layout>
  );
};

export default managedItServices;
